import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, AlertCircle } from 'lucide-react';
import { kycService } from '../../services/kycService';
import { companyService } from '../../services/companyService';
import CustomSelect from '../shared/CustomSelect';

const KycForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: '',
        applicableClientType: '',
        companyId: '',
        mandatory: false
    });

    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchCompanies();
        if (isEditMode) {
            fetchRequirementDetails();
        }
    }, []);

    const fetchCompanies = async () => {
        try {
            const response = await companyService.getAll();
            if (response.success && response.data) {
                setCompanies(response.data);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const fetchRequirementDetails = async () => {
        try {
            setLoading(true);
            const response = await kycService.kycRequirements.getById(id);
            if (response.success && response.data) {
                const req = response.data;
                setFormData({
                    name: req.name || '',
                    description: req.description || '',
                    type: req.type || '',
                    applicableClientType: req.applicableClientType || '',
                    companyId: req.companyId || req.company?.id || '',
                    mandatory: req.mandatory || false
                });
            }
        } catch (error) {
            console.error('Error fetching requirement:', error);
            setError('Failed to load requirement details');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');

        try {
            let response;
            if (isEditMode) {
                response = await kycService.kycRequirements.update(id, formData);
            } else {
                response = await kycService.kycRequirements.create(formData);
            }

            if (response.success) {
                navigate('/compliance/kyc/settings');
            } else {
                setError(response.message || 'Failed to save requirement');
            }
        } catch (error) {
            console.error('Error saving requirement:', error);
            setError(error.message || 'Failed to save requirement');
        } finally {
            setSaving(false);
        }
    };

    const companyOptions = companies.map(company => ({
        value: company.id,
        label: company.name
    }));

    const kycTypeOptions = [
        { value: 'NATIONAL_ID', label: 'National ID' },
        { value: 'PASSPORT', label: 'Passport' },
        { value: 'DRIVERS_LICENSE', label: 'Drivers License' },
        { value: 'KRA_PIN', label: 'KRA PIN' },
        { value: 'BIRTH_CERTIFICATE', label: 'Birth Certificate' },
        { value: 'ALIEN_CARD', label: 'Alien Card' },
        { value: 'PHOTOGRAPH', label: 'Photograph' },
        { value: 'UTILITY_BILL', label: 'Utility Bill' },
        { value: 'BANK_STATEMENT', label: 'Bank Statement' },
        { value: 'EMPLOYMENT_LETTER', label: 'Employment Letter' },
        { value: 'SOURCE_OF_FUNDS', label: 'Source of Funds' },
        { value: 'SOURCE_OF_WEALTH', label: 'Source of Wealth' },
        { value: 'CERTIFICATE_OF_INCORPORATION', label: 'Certificate of Incorporation' },
        { value: 'BUSINESS_LICENSE', label: 'Business License' },
        { value: 'TAX_REGISTRATION', label: 'Tax Registration' },
        { value: 'COMPANY_BANK_STATEMENT', label: 'Company Bank Statement' },
        { value: 'MEMORANDUM_OF_ASSOCIATION', label: 'Memorandum of Association' },
        { value: 'ARTICLES_OF_ASSOCIATION', label: 'Articles of Association' },
        { value: 'BOARD_RESOLUTION', label: 'Board Resolution' },
        { value: 'DIRECTOR_IDENTIFICATION', label: 'Director Identification' },
        { value: 'COMPANY_PROFILE', label: 'Company Profile' },
        { value: 'AUDITED_FINANCIALS', label: 'Audited Financials' },
        { value: 'CR12_FORM', label: 'CR12 Form' },
        { value: 'BO_DECLARATION', label: 'BO Declaration' },
        { value: 'BO_REGISTER', label: 'BO Register' },
        { value: 'OWNERSHIP_CHAIN', label: 'Ownership Chain' },
        { value: 'OTHER', label: 'Other' }
    ];

    const clientTypeOptions = [
        { value: 'RETAIL', label: 'Retail' },
        { value: 'CORPORATE', label: 'Corporate' }
    ];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/compliance/kyc/settings')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-2xl font-semibold text-gray-900">
                        {isEditMode ? 'Edit KYC Requirement' : 'Add KYC Requirement'}
                    </h2>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Requirement Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Requirement Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., National ID Card"
                                    required
                                />
                            </div>

                            {/* Company */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Company <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    value={formData.companyId}
                                    onChange={(value) => setFormData(prev => ({ ...prev, companyId: value }))}
                                    options={companyOptions}
                                    placeholder="Select a company"
                                    searchable
                                />
                            </div>

                            {/* KYC Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Document Type <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    value={formData.type}
                                    onChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                                    options={kycTypeOptions}
                                    placeholder="Select document type"
                                />
                            </div>

                            {/* Client Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Applicable Client Type <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    value={formData.applicableClientType}
                                    onChange={(value) => setFormData(prev => ({ ...prev, applicableClientType: value }))}
                                    options={clientTypeOptions}
                                    placeholder="Select client type"
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                rows="3"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Describe this KYC requirement..."
                            />
                        </div>

                        {/* Mandatory */}
                        <div className="mt-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.mandatory}
                                    onChange={(e) => setFormData(prev => ({ ...prev, mandatory: e.target.checked }))}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">Mandatory Requirement</span>
                            </label>
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex items-center gap-3 pt-6 border-t">
                        <button
                            type="submit"
                            disabled={saving || !formData.name || !formData.companyId || !formData.type || !formData.applicableClientType}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    {isEditMode ? 'Update Requirement' : 'Create Requirement'}
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/compliance/kyc/settings')}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default KycForm;
